import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronDown, Heart, Shield, BadgeCheck } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1762175029293-bb02b2e85e15?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
  "https://images.unsplash.com/photo-1593113598332-cd288d649433?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
  "https://images.unsplash.com/photo-1542810634-71277d95dcbb?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
];

const Hero = () => {
  const { t } = useLanguage();
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % HERO_IMAGES.length), 6000);
    return () => clearInterval(id);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="relative min-h-[100vh] flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Crossfading background carousel */}
      <div className="absolute inset-0">
        <AnimatePresence mode="sync">
          <motion.img
            key={idx}
            src={HERO_IMAGES[idx]}
            alt="Fondation Cœur Solidaire"
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 1.2 }, scale: { duration: 7, ease: "linear" } }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 hero-overlay" />
      </div>

      <div className="container-narrow relative z-10 py-32 md:py-40">
        <div className="max-w-3xl">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="eyebrow text-[#E8C77E]"
            data-testid="hero-eyebrow"
          >
            {t.hero.eyebrow}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-serif text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05] mt-5 font-medium"
            data-testid="hero-title"
          >
            {t.hero.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="text-white/85 text-lg md:text-xl mt-6 max-w-2xl leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="font-serif italic text-[#E8C77E] text-xl md:text-2xl mt-3"
            data-testid="hero-tagline"
          >
            « {t.hero.tagline} »
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.45 }}
            className="flex flex-col sm:flex-row gap-4 mt-10"
          >
            <button
              onClick={() => scrollTo("donate")}
              className="btn-primary group"
              data-testid="hero-cta-donate"
            >
              {t.hero.ctaPrimary}
              <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollTo("mission")}
              className="btn-outline-light"
              data-testid="hero-cta-mission"
            >
              {t.hero.ctaSecondary}
            </button>
          </motion.div>

          {/* Trust badges row */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="flex flex-wrap gap-3 mt-10"
            data-testid="hero-trust-badges"
          >
            <TrustBadge Icon={BadgeCheck} label={t.hero.badges.audited} />
            <TrustBadge Icon={Shield} label={t.hero.badges.secure} />
            <TrustBadge Icon={Heart} label={t.hero.badges.programs} />
          </motion.div>
        </div>

        {/* Carousel dots */}
        <div className="flex gap-2 mt-12">
          {HERO_IMAGES.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`slide-${i + 1}`}
              className={`h-1 rounded-full transition-all ${i === idx ? "w-10 bg-[#E8C77E]" : "w-5 bg-white/30 hover:bg-white/50"}`}
              data-testid={`hero-dot-${i}`}
            />
          ))}
        </div>
      </div>

      <button
        onClick={() => scrollTo("mission")}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70 hover:text-white transition-colors animate-bounce"
        aria-label="Scroll down"
        data-testid="hero-scroll-down"
      >
        <ChevronDown size={28} />
      </button>
    </section>
  );
};

const TrustBadge = ({ Icon, label }) => (
  <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1.5 text-xs text-white/85 backdrop-blur-sm">
    <Icon size={12} className="text-[#E8C77E]" /> {label}
  </span>
);

export default Hero;
